import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as Sentry from "@sentry/react";
import { X, ImagePlus, Trash2 } from "lucide-react";
import {
  LOCATION_ID_PATTERN,
  slugifyLocationId,
  createLocation,
  updateLocation,
  uploadLocationImage,
  deleteLocationImage,
} from "services/locationService";

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const inputClass =
  "w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-50 focus:outline-none focus:ring focus:border-amber-400 disabled:opacity-60";
const labelClass = "block mb-1 text-sm font-medium text-slate-200";
const hintClass = "text-xs text-slate-400 mt-1";

const OPTIONS = [
  {
    key: "active",
    label: "Actif",
    hint: "Affiché dans le menu « S'inscrire aux hebdos » et sélectionnable comme tournoi hebdo sur la carte.",
  },
  {
    key: "usesRankings",
    label: "Utilise les classements",
    hint: "Les résultats des tournois alimentent le classement du lieu.",
  },
  {
    key: "displayRanking",
    label: "Afficher les classements",
    hint: "Le classement est visible sur la page du lieu.",
  },
  {
    key: "allowMyMatches",
    label: "Permettre « Mes matchs »",
    hint: "Les joueurs consultent et entrent les scores de leurs propres matchs.",
  },
  {
    key: "allowCrossPool",
    label: "Permettre les pools croisés",
    hint: "Option d'élimination croisée entre deux divisions.",
  },
  {
    key: "testEnv",
    label: "Lieu de test",
    hint: "Ajoute un générateur de scores aléatoires sur la page de progression des tournois.",
  },
];

/**
 * Master-only create / edit modal for a location. `location` is null when creating.
 * `locations` is the full list, used to refuse a taken id and to suggest admin groups.
 * The id is chosen at creation and cannot change afterwards (URLs, tournament ids).
 */
const LocationAdminForm = ({ location, locations = [], onClose, onSaved }) => {
  const isEdit = !!location;
  const [name, setName] = useState(location?.name || "");
  const [table, setTable] = useState(location?.table || "");
  const [town, setTown] = useState(location?.town || "");
  const [id, setId] = useState(location?.id || "");
  const [idTouched, setIdTouched] = useState(isEdit);
  const [facebook, setFacebook] = useState(location?.facebook || "");
  const [options, setOptions] = useState(() =>
    Object.fromEntries(
      OPTIONS.map((o) => [o.key, location ? !!location[o.key] : o.key === "active"])
    )
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(location?.image || null);
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);

  // Includes discarded locations: their id stays taken (tournaments still point to it).
  const locationsById = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.id, l])),
    [locations]
  );

  // Close on Escape, lock the page scroll while open, clean up preview URLs.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, saving]);

  useEffect(
    () => () => objectUrlRef.current && URL.revokeObjectURL(objectUrlRef.current),
    []
  );

  // While creating, the id follows the name and table until edited by hand.
  const suggestIdFrom = (nextName, nextTable) => {
    if (isEdit || idTouched) return;
    setId(slugifyLocationId([nextName, nextTable].filter(Boolean).join(" ")));
  };

  const handleIdChange = (value) => {
    setIdTouched(true);
    setId(value.toLowerCase().replace(/\s+/g, "-"));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Format d'image non supporté. Utilisez JPEG, PNG ou WebP.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("Image trop volumineuse (max 5 Mo).");
      return;
    }
    setError("");
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreview(objectUrlRef.current);
    setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (location?.image) setRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmedId = id.trim();
    if (!name.trim()) return setError("Le nom est requis.");
    if (!town.trim()) return setError("La ville est requise.");
    if (!isEdit) {
      if (!LOCATION_ID_PATTERN.test(trimmedId))
        return setError(
          "L'identifiant ne peut contenir que des lettres minuscules sans accents, des chiffres et des tirets (ex. : tapis-vert-leonhart)."
        );
      const taken = locationsById[trimmedId];
      if (taken)
        return setError(
          `L'identifiant « ${trimmedId} » est déjà utilisé par ${
            taken.discarded ? "un lieu supprimé" : "un autre lieu"
          } (${taken.name}).`
        );
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        table: table.trim(),
        town: town.trim(),
        facebook: facebook.trim(),
        ...options,
      };

      const locationId = isEdit ? location.id : trimmedId;
      if (isEdit) await updateLocation(locationId, payload);
      else await createLocation(locationId, payload);

      if (removeImage && location?.image) await deleteLocationImage(locationId, location.image);
      if (imageFile) await uploadLocationImage(locationId, imageFile);

      onSaved(locationId);
    } catch (err) {
      if (err?.code === "location/already-exists") {
        setError(`L'identifiant « ${trimmedId} » est déjà utilisé par un autre lieu.`);
      } else {
        Sentry.captureException(err);
        console.error("Error saving location:", err);
        setError("Une erreur est survenue lors de la sauvegarde.");
      }
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center p-2 sm:p-6 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-lg shadow-2xl text-slate-50 my-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h2 className="text-xl font-bold">
            {isEdit ? `Modifier « ${location.name} »` : "Ajouter un lieu"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition"
            title="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {error && (
            <p className="text-red-400 font-medium bg-red-950/40 border border-red-900 rounded px-3 py-2">
              {error}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Left column: identity */}
            <div className="space-y-4">
              <div>
                <label className={labelClass} htmlFor="loc-name">
                  Nom *
                </label>
                <input
                  id="loc-name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    suggestIdFrom(e.target.value, table);
                  }}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="loc-table">
                  Table
                </label>
                <input
                  id="loc-table"
                  type="text"
                  value={table}
                  onChange={(e) => {
                    setTable(e.target.value);
                    suggestIdFrom(name, e.target.value);
                  }}
                  placeholder="Bonzini, Leonhart, Tornado…"
                  className={inputClass}
                />
                <p className={hintClass}>
                  Les lieux du même nom sont regroupés dans le menu, par table.
                </p>
              </div>

              <div>
                <label className={labelClass} htmlFor="loc-town">
                  Ville *
                </label>
                <input
                  id="loc-town"
                  type="text"
                  value={town}
                  onChange={(e) => setTown(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="loc-id">
                  Identifiant {isEdit ? "" : "*"}
                </label>
                <input
                  id="loc-id"
                  type="text"
                  value={id}
                  onChange={(e) => handleIdChange(e.target.value)}
                  disabled={isEdit}
                  placeholder="tapis-vert-leonhart"
                  className={`${inputClass} font-mono text-sm`}
                  required={!isEdit}
                />
                <p className={hintClass}>
                  {isEdit
                    ? "L'identifiant ne peut pas être modifié (il fait partie des liens et des tournois)."
                    : "Ne pourra plus être modifié : il fait partie des liens et des tournois."}{" "}
                  Lien : <span className="font-mono">/locations/{id || "…"}</span>
                </p>
              </div>

              <div>
                <label className={labelClass} htmlFor="loc-facebook">
                  Page Facebook
                </label>
                <input
                  id="loc-facebook"
                  type="url"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                  placeholder="https://facebook.com/..."
                  className={inputClass}
                />
              </div>
            </div>

            {/* Right column: image + options */}
            <div className="space-y-4">
              <div>
                <span className={labelClass}>Image</span>
                {imagePreview ? (
                  <div className="relative group">
                    <img src={imagePreview} alt="" className="w-full h-40 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-black/60 hover:bg-red-600 text-white text-xs transition"
                      title="Retirer l'image"
                    >
                      <Trash2 size={14} /> Retirer
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-28 rounded-lg border-2 border-dashed border-slate-600 hover:border-slate-400 flex flex-col items-center justify-center text-slate-400 hover:text-slate-300 transition"
                  >
                    <ImagePlus size={28} className="mb-1" />
                    <span className="text-sm">Ajouter une image</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>

              <fieldset className="space-y-3">
                <legend className={labelClass}>Options</legend>
                {OPTIONS.map((o) => (
                  <label key={o.key} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options[o.key]}
                      onChange={(e) =>
                        setOptions((prev) => ({ ...prev, [o.key]: e.target.checked }))
                      }
                      className="mt-1 h-4 w-4 accent-amber-500 flex-shrink-0"
                    />
                    <span>
                      <span className="block text-sm font-semibold">{o.label}</span>
                      <span className="block text-xs text-slate-400 leading-snug">{o.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap justify-end gap-3 px-5 py-4 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="bg-slate-700 hover:bg-slate-600 text-white px-5 py-2 rounded-md font-semibold transition disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-white px-5 py-2 rounded-md font-semibold transition disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : isEdit ? "Mettre à jour" : "Ajouter"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default LocationAdminForm;
